import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
} from '@revelio/data-access';
import { NoData } from '@revelio/replots';

import { useCustomSkillTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../../filters/types/filters';
import { getCompositionV3BreakdownOptions } from '../../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getCompositionV3Corrections,
  getCompositionV3Filters,
} from '../../../../filters/utils/composition-v3/getCompositionV3Filters';
import { getCompositionV3Taxonomies } from '../../../../filters/utils/composition-v3/getCompositionV3Taxonomies';
import {
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../../filters/utils/composition-v3/getCompositionV3Variables';
import { isCompositionV3CustomSkillTaxonomyReady } from '../../../../filters/utils/composition-v3/is-composition-v3-custom-skill-taxonomy-ready';
import { mergeTopNPrimarySelectionIntoV3Filters } from '../../../../filters/utils/composition-v3/merge-top-n-primary-selection-v3';
import {
  positionsEntityKindToEntKind,
  stripEntitiesFromFilters,
} from '../../../../filters/utils/composition-v3/role-skill-ent-kind';
import { getSecondaryPrimaryDimension } from '../../../../filters/utils/get-secondary-primary-dimension';
import { useChartPrimaryFilter } from '../../../../hooks/useChartPrimaryFilter';
import { ChartFallback } from '../../../Chart/ChartFallback';
import { useChart } from '../../../Chart/ChartProvider';
import type { CompositionMetricValues } from '../../../PageConfigProvider/types/metric';
import { isSingleEntityChart } from '../../../PageState/utils/is-single-entity-chart';
import {
  type SupportedSecondaryDimensions,
  type TopEntity,
  TopNDataProvider,
} from '../../TopNDataProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
} from '../../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../../utils/data-provider-results-utils';
import {
  type CompositionTopNDataSetTypeV3,
  convertCompositionTopNDataV3,
} from './convert-composition-top-n-data-v3';

type CompositionV3TopNQueryProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: CompositionMetricValues;
  isTimeseries?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<CompositionTopNDataSetTypeV3 | undefined>;
};

export const CompositionV3TopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  isTimeseries,
  exchangeRate,
  children,
}: CompositionV3TopNQueryProps) => {
  return (
    <TopNDataProvider
      primaryDimension={primaryDimension}
      secondaryDimension={secondaryDimension}
    >
      {({ data, status }) => {
        if (!data || status === DataProviderQueryStatus.Fetching) {
          return <ChartFallback status={status} />;
        }
        if (data.length === 0) {
          return <NoData />;
        }
        return (
          <CompositionV3MultiQuery
            primaryDimension={primaryDimension}
            secondaryDimension={secondaryDimension}
            topEntities={data}
            metric={metric}
            isTimeseries={isTimeseries}
            exchangeRate={exchangeRate}
          >
            {({ data: compData, status: compStatus }) => (
              <ChartFallback status={compStatus}>
                {children({ data: compData, status: compStatus })}
              </ChartFallback>
            )}
          </CompositionV3MultiQuery>
        );
      }}
    </TopNDataProvider>
  );
};

type PerEntityResult = {
  dim: string;
  data: CompositionV3DataQuery['composition_v3'] | undefined;
  status: DataProviderQueryStatus;
};

const CompositionV3MultiQuery = ({
  primaryDimension,
  secondaryDimension,
  metric,
  topEntities,
  isTimeseries,
  exchangeRate,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: CompositionMetricValues;
  topEntities: TopEntity[];
  isTimeseries?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<CompositionTopNDataSetTypeV3 | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { activeFilterMap, geographyMode } = useFilterContext();
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const { item } = useChart();
  const selections = primaryFilter?.selections;
  const primarySelections =
    selections?.map((selection) => ({
      [selection.levelId]: [Number(selection?.id?.split('_')?.slice(-1)?.[0])],
    })) ?? [];
  const chartPrimaryFilter = useChartPrimaryFilter({ primaryDimension });
  const isSingleEntity = isSingleEntityChart(item);
  const filteredPrimarySelections = isSingleEntity
    ? [chartPrimaryFilter]
    : primarySelections;

  const effectiveFiltersNormalized =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const {
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
  } = useCustomSkillTaxonomyTree();
  const customSkillDeepestLevel = getCustomSkillDeepestLevelFromFilters(
    effectiveFiltersNormalized
  );
  const isCompositionSkillBreakdownReady =
    isCompositionV3CustomSkillTaxonomyReady({
      effectiveFilters: effectiveFiltersNormalized,
      customSkillTree,
      isCustomSkillLevelMapReady,
      isCustomSkillTaxonomyPending,
    });

  const compositionFilters = filteredPrimarySelections.map(
    (primarySelection): CompositionV3DataQueryVariables => {
      const baseFilters = getCompositionV3Filters({
        primaryDimension: getSecondaryPrimaryDimension(secondaryDimension),
        isSnapshot: !isTimeseries,
        effectiveFilters: effectiveFiltersNormalized,
      });
      const mergedFilters = mergeTopNPrimarySelectionIntoV3Filters(
        baseFilters,
        primarySelection as Record<string, number[] | undefined>
      );
      const entities = topEntities.map((ent) => ({
        EntID: ent.EntID,
        EntType: positionsEntityKindToEntKind(ent.EntType),
      }));
      const filters = stripEntitiesFromFilters(mergedFilters, entities);
      const corrections = getCompositionV3Corrections(
        effectiveFiltersNormalized,
        activeFilterMap
      );
      const tax = getCompositionV3Taxonomies(
        effectiveFiltersNormalized,
        activeFilterMap
      );
      const breakdown_options = getCompositionV3BreakdownOptions({
        compositionFilters: filters,
        customSkillTree,
        customSkillDeepestLevel,
        geographyMode,
      });
      return {
        entities,
        filters,
        corrections,
        role_taxonomy: tax.role_taxonomy,
        skill_taxonomy: tax.skill_taxonomy,
        breakdown_options,
      };
    }
  );

  if (!isCompositionSkillBreakdownReady) {
    return children({
      data: undefined,
      status: DataProviderQueryStatus.Fetching,
    });
  }

  const renderQueryChain = (
    index: number,
    accumulated: PerEntityResult[]
  ): React.ReactElement => {
    const variables = compositionFilters[index];
    if (variables === undefined) {
      const status = combineQueryStatus(
        accumulated.map((entry) => entry.status)
      );
      const data =
        status === DataProviderQueryStatus.Success
          ? convertCompositionTopNDataV3({
              data: accumulated,
              isGrouped: item.plot?.chartType !== 'bar',
              metric,
              exchangeRate,
            })
          : undefined;
      return <>{children({ data, status })}</>;
    }

    return (
      <CompositionV3SingleEntityQuery
        variables={variables}
        dim={selections?.[index]?.name ?? ''}
      >
        {(result) => renderQueryChain(index + 1, [...accumulated, result])}
      </CompositionV3SingleEntityQuery>
    );
  };

  return renderQueryChain(0, []);
};

const CompositionV3SingleEntityQuery = ({
  variables,
  dim,
  children,
}: {
  variables: CompositionV3DataQueryVariables;
  dim: string;
  children: (result: PerEntityResult) => React.ReactElement;
}) => {
  const [{ data, status }] = useUrqlQueryWithStatus<
    CompositionV3DataQuery,
    CompositionV3DataQueryVariables
  >({
    query: COMPOSITION_GET_DATA_V3,
    variables,
  });
  return children({ dim, data: data?.composition_v3, status });
};
